import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Mail, Send, Building2, Users, Briefcase, CheckCircle, AlertCircle, Globe, Phone, Linkedin, Instagram, Youtube } from 'lucide-react';
import axios from 'axios';
import { useSiteContact } from '../hooks/useSiteContact';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const WhatsAppIcon = ({ size = 24, className = "", style }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="currentColor" className={className} style={style}>
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

const ContactPage = () => {
  const [searchParams] = useSearchParams();
  const contact = useSiteContact();
  const [activeTab, setActiveTab] = useState('general');
  const [formData, setFormData] = useState({
    name: '', email: '', phone: '', subject: '', message: ''
  });
  const [investorFormData, setInvestorFormData] = useState({
    name: '', email: '', phone: '', organization: '', subject: '', message: ''
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab === 'investor') setActiveTab('investor');
  }, [searchParams]);

  const validateEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  const validatePhone = (phone) => /^\+?[\d\s-]{10,}$/.test(phone.replace(/\s/g, ''));

  const handleGeneralSubmit = async (e) => {
    e.preventDefault();
    setError(''); setSuccess('');
    if (!formData.name.trim()) { setError('Please enter your name'); return; }
    if (!validateEmail(formData.email)) { setError('Please enter a valid email address'); return; }
    if (formData.phone && !validatePhone(formData.phone)) { setError('Please enter a valid phone number'); return; }
    if (!formData.subject.trim()) { setError('Please enter a subject'); return; }
    if (!formData.message.trim()) { setError('Please enter your message'); return; }

    setLoading(true);
    try {
      await axios.post(`${API}/contact/submit`, { ...formData, enquiry_type: 'general' });
      setSuccess('Thank you for reaching out! We will get back to you soon.');
      setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to submit. Please try again.');
    } finally { setLoading(false); }
  };

  const handleInvestorSubmit = async (e) => {
    e.preventDefault();
    setError(''); setSuccess('');
    if (!investorFormData.name.trim()) { setError('Please enter your name'); return; }
    if (!validateEmail(investorFormData.email)) { setError('Please enter a valid email address'); return; }
    if (investorFormData.phone && !validatePhone(investorFormData.phone)) { setError('Please enter a valid phone number'); return; }
    if (!investorFormData.message.trim()) { setError('Please enter your message'); return; }

    setLoading(true);
    try {
      await axios.post(`${API}/contact/submit`, {
        ...investorFormData, subject: investorFormData.subject || 'Investor Enquiry', enquiry_type: 'investor'
      });
      setSuccess('Thank you for your interest! Our team will contact you shortly.');
      setInvestorFormData({ name: '', email: '', phone: '', organization: '', subject: '', message: '' });
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to submit. Please try again.');
    } finally { setLoading(false); }
  };

  const inputClass = "w-full px-4 py-3 text-sm border transition-all focus:outline-none focus:ring-2 focus:ring-[#002FA7]/20 focus:border-[#002FA7]";

  return (
    <div style={{ background: '#FAFAFA', fontFamily: "'IBM Plex Sans', sans-serif" }} className="min-h-screen">
      {/* Hero */}
      <div style={{ background: '#0A0A0A' }} className="relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.03]" style={{
          backgroundImage: 'repeating-linear-gradient(90deg, #fff 0px, #fff 1px, transparent 1px, transparent 80px), repeating-linear-gradient(0deg, #fff 0px, #fff 1px, transparent 1px, transparent 80px)'
        }} />
        <div className="max-w-5xl mx-auto px-6 py-20 lg:py-28 relative">
          <p className="text-[10px] font-bold tracking-[0.25em] uppercase mb-4" style={{ color: 'rgba(255,255,255,0.4)', fontFamily: "'IBM Plex Mono', monospace" }}>
            Contact
          </p>
          <h1 className="text-4xl sm:text-5xl font-black tracking-tight text-white mb-4" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
            Get in Touch
          </h1>
          <p className="text-base max-w-lg" style={{ color: 'rgba(255,255,255,0.55)', lineHeight: '1.7' }}>
            Have a question about EvalentX? We're here to help. Reach out and our team will respond within 24 hours.
          </p>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 py-12 lg:py-16">
        {/* Contact Info Row */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-12">
          {[
            { icon: Mail, label: 'Email', value: 'support@evalentx.com', href: 'mailto:support@evalentx.com' },
            { icon: Phone, label: 'Phone', value: contact.phone, href: contact.phone_href },
            contact.whatsapp_enabled
              ? { icon: WhatsAppIcon, label: 'WhatsApp', value: 'Chat with us', href: contact.whatsapp_link, accent: true }
              : { icon: Globe, label: 'Website', value: 'www.evalentx.com', href: 'https://www.evalentx.com' },
            contact.whatsapp_channel_url
              ? { icon: WhatsAppIcon, label: 'Channel', value: 'Join WhatsApp Channel', href: contact.whatsapp_channel_url }
              : contact.whatsapp_enabled && { icon: Globe, label: 'Website', value: 'www.evalentx.com', href: 'https://www.evalentx.com' },
          ].filter(Boolean).map((item) => (
            <a key={item.label} href={item.href} target={item.href.startsWith('http') ? '_blank' : undefined} rel="noopener noreferrer"
              className="flex items-center gap-3 p-4 bg-white transition-all group"
              style={{ border: '1px solid #E4E4E7', borderRadius: '2px' }}
              data-testid={`contact-info-${item.label.toLowerCase()}`}
            >
              <div className="w-10 h-10 flex items-center justify-center flex-shrink-0" style={{ background: item.accent ? '#25D366' : '#F4F4F5', borderRadius: '2px' }}>
                <item.icon size={18} style={{ color: item.accent ? '#FFFFFF' : '#002FA7' }} />
              </div>
              <div>
                <p className="text-[10px] font-bold tracking-[0.15em] uppercase" style={{ color: '#71717A' }}>{item.label}</p>
                <p className="text-sm font-medium group-hover:text-[#002FA7] transition-colors" style={{ color: '#0A0A0A' }}>{item.value}</p>
              </div>
            </a>
          ))}
        </div>

        {/* Social Row */}
        <div className="flex items-center gap-3 mb-12">
          <span className="text-[10px] font-bold tracking-[0.2em] uppercase mr-2" style={{ color: '#A1A1AA' }}>Follow us</span>
          {[
            { icon: Linkedin, href: 'https://www.linkedin.com/company/evalentx/about/', label: 'LinkedIn' },
            { icon: Instagram, href: 'https://www.instagram.com/evalentx?utm_source=qr&igsh=OHZrejJ0a3dwOXlh', label: 'Instagram' },
            { icon: Youtube, href: 'https://www.youtube.com/@EvalentX', label: 'YouTube' },
          ].map((s) => (
            <a key={s.label} href={s.href} target="_blank" rel="noopener noreferrer"
              className="w-9 h-9 flex items-center justify-center transition-all hover:border-[#002FA7] hover:text-[#002FA7]"
              style={{ border: '1px solid #E4E4E7', borderRadius: '2px', color: '#71717A' }}
              title={s.label} data-testid={`contact-social-${s.label.toLowerCase()}`}
            >
              <s.icon size={16} />
            </a>
          ))}
        </div>

        {/* Tab Switcher */}
        <div className="flex gap-0 mb-8" style={{ borderBottom: '1px solid #E4E4E7' }}>
          <button
            onClick={() => { setActiveTab('general'); setError(''); setSuccess(''); }}
            className="flex items-center gap-2 px-5 py-3 text-sm font-semibold transition-colors"
            style={{
              color: activeTab === 'general' ? '#002FA7' : '#71717A',
              borderBottom: activeTab === 'general' ? '2px solid #002FA7' : '2px solid transparent',
            }}
            data-testid="contact-tab-general"
          >
            <Users size={16} /> General Enquiry
          </button>
          <button
            onClick={() => { setActiveTab('investor'); setError(''); setSuccess(''); }}
            className="flex items-center gap-2 px-5 py-3 text-sm font-semibold transition-colors"
            style={{
              color: activeTab === 'investor' ? '#002FA7' : '#71717A',
              borderBottom: activeTab === 'investor' ? '2px solid #002FA7' : '2px solid transparent',
            }}
            data-testid="contact-tab-investor"
          >
            <Briefcase size={16} /> Investor Corner
          </button>
        </div>

        {/* Messages */}
        {success && (
          <div className="mb-6 p-4 flex items-center gap-3" style={{ background: '#F0FDF4', border: '1px solid #BBF7D0', borderRadius: '2px' }}>
            <CheckCircle size={20} style={{ color: '#059669' }} />
            <p className="text-sm font-medium" style={{ color: '#065F46' }}>{success}</p>
          </div>
        )}
        {error && (
          <div className="mb-6 p-4 flex items-center gap-3" style={{ background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: '2px' }}>
            <AlertCircle size={20} style={{ color: '#DC2626' }} />
            <p className="text-sm font-medium" style={{ color: '#991B1B' }}>{error}</p>
          </div>
        )}

        {/* General Form */}
        {activeTab === 'general' && (
          <div className="bg-white p-8 lg:p-10" style={{ border: '1px solid #E4E4E7', borderRadius: '2px' }}>
            <div className="mb-8">
              <h2 className="text-xl font-bold tracking-tight" style={{ color: '#0A0A0A' }}>Send us a Message</h2>
              <p className="text-sm mt-1" style={{ color: '#71717A' }}>We'll get back to you within 24-48 hours</p>
            </div>

            <form onSubmit={handleGeneralSubmit} className="space-y-5">
              <div className="grid md:grid-cols-2 gap-5">
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-[0.1em] mb-2" style={{ color: '#52525B' }}>Full Name *</label>
                  <input type="text" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="John Doe" data-testid="contact-name-input"
                    className={inputClass} style={{ border: '1px solid #E4E4E7', borderRadius: '2px', color: '#0A0A0A' }} />
                </div>
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-[0.1em] mb-2" style={{ color: '#52525B' }}>Email Address *</label>
                  <input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="john@example.com" data-testid="contact-email-input"
                    className={inputClass} style={{ border: '1px solid #E4E4E7', borderRadius: '2px', color: '#0A0A0A' }} />
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-5">
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-[0.1em] mb-2" style={{ color: '#52525B' }}>Phone Number</label>
                  <input type="tel" value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    placeholder="+91 98765 43210" data-testid="contact-phone-input"
                    className={inputClass} style={{ border: '1px solid #E4E4E7', borderRadius: '2px', color: '#0A0A0A' }} />
                </div>
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-[0.1em] mb-2" style={{ color: '#52525B' }}>Subject *</label>
                  <input type="text" value={formData.subject} onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                    placeholder="Registration Query" data-testid="contact-subject-input"
                    className={inputClass} style={{ border: '1px solid #E4E4E7', borderRadius: '2px', color: '#0A0A0A' }} />
                </div>
              </div>
              <div>
                <label className="block text-xs font-semibold uppercase tracking-[0.1em] mb-2" style={{ color: '#52525B' }}>Message *</label>
                <textarea value={formData.message} onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  placeholder="Tell us how we can help you..." rows={5} data-testid="contact-message-input"
                  className={`${inputClass} resize-none`} style={{ border: '1px solid #E4E4E7', borderRadius: '2px', color: '#0A0A0A' }} />
              </div>
              <button type="submit" disabled={loading} data-testid="contact-submit-btn"
                className="flex items-center justify-center gap-2 w-full sm:w-auto px-8 py-3 text-sm font-bold text-white transition-all disabled:opacity-50"
                style={{ background: '#002FA7', borderRadius: '2px' }}>
                {loading ? (
                  <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Sending...</>
                ) : (
                  <><Send size={16} /> Send Message</>
                )}
              </button>
            </form>
          </div>
        )}

        {/* Investor Form */}
        {activeTab === 'investor' && (
          <div className="bg-white p-8 lg:p-10" style={{ border: '1px solid #E4E4E7', borderRadius: '2px' }}>
            <div className="mb-6">
              <h2 className="text-xl font-bold tracking-tight" style={{ color: '#0A0A0A' }}>Investor Corner</h2>
              <p className="text-sm mt-1" style={{ color: '#71717A' }}>Partner with us to shape the future of tech education</p>
            </div>
            <div className="p-4 mb-8" style={{ background: '#F0FDF4', border: '1px solid #BBF7D0', borderRadius: '2px' }}>
              <p className="text-sm" style={{ color: '#065F46' }}>
                <strong>Why invest in EvalentX?</strong> We're building India's largest student tech community, connecting talented minds with industry opportunities through hackathons, workshops, and innovation challenges.
              </p>
            </div>

            <form onSubmit={handleInvestorSubmit} className="space-y-5">
              <div className="grid md:grid-cols-2 gap-5">
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-[0.1em] mb-2" style={{ color: '#52525B' }}>Full Name *</label>
                  <input type="text" value={investorFormData.name} onChange={(e) => setInvestorFormData({ ...investorFormData, name: e.target.value })}
                    placeholder="John Doe" data-testid="investor-name-input"
                    className={inputClass} style={{ border: '1px solid #E4E4E7', borderRadius: '2px', color: '#0A0A0A' }} />
                </div>
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-[0.1em] mb-2" style={{ color: '#52525B' }}>Email Address *</label>
                  <input type="email" value={investorFormData.email} onChange={(e) => setInvestorFormData({ ...investorFormData, email: e.target.value })}
                    placeholder="john@venture.com" data-testid="investor-email-input"
                    className={inputClass} style={{ border: '1px solid #E4E4E7', borderRadius: '2px', color: '#0A0A0A' }} />
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-5">
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-[0.1em] mb-2" style={{ color: '#52525B' }}>Phone Number</label>
                  <input type="tel" value={investorFormData.phone} onChange={(e) => setInvestorFormData({ ...investorFormData, phone: e.target.value })}
                    placeholder="+91 98765 43210" data-testid="investor-phone-input"
                    className={inputClass} style={{ border: '1px solid #E4E4E7', borderRadius: '2px', color: '#0A0A0A' }} />
                </div>
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-[0.1em] mb-2" style={{ color: '#52525B' }}>Organization / Fund</label>
                  <input type="text" value={investorFormData.organization} onChange={(e) => setInvestorFormData({ ...investorFormData, organization: e.target.value })}
                    placeholder="ABC Ventures" data-testid="investor-org-input"
                    className={inputClass} style={{ border: '1px solid #E4E4E7', borderRadius: '2px', color: '#0A0A0A' }} />
                </div>
              </div>
              <div>
                <label className="block text-xs font-semibold uppercase tracking-[0.1em] mb-2" style={{ color: '#52525B' }}>Subject</label>
                <input type="text" value={investorFormData.subject} onChange={(e) => setInvestorFormData({ ...investorFormData, subject: e.target.value })}
                  placeholder="Investment Opportunity / Partnership" data-testid="investor-subject-input"
                  className={inputClass} style={{ border: '1px solid #E4E4E7', borderRadius: '2px', color: '#0A0A0A' }} />
              </div>
              <div>
                <label className="block text-xs font-semibold uppercase tracking-[0.1em] mb-2" style={{ color: '#52525B' }}>Message *</label>
                <textarea value={investorFormData.message} onChange={(e) => setInvestorFormData({ ...investorFormData, message: e.target.value })}
                  placeholder="Tell us about your interest in EvalentX and how you'd like to partner with us..." rows={5} data-testid="investor-message-input"
                  className={`${inputClass} resize-none`} style={{ border: '1px solid #E4E4E7', borderRadius: '2px', color: '#0A0A0A' }} />
              </div>
              <button type="submit" disabled={loading} data-testid="investor-submit-btn"
                className="flex items-center justify-center gap-2 w-full sm:w-auto px-8 py-3 text-sm font-bold text-white transition-all disabled:opacity-50"
                style={{ background: '#059669', borderRadius: '2px' }}>
                {loading ? (
                  <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Sending...</>
                ) : (
                  <><Send size={16} /> Submit Enquiry</>
                )}
              </button>
            </form>
          </div>
        )}

        {/* FAQ */}
        <div className="mt-16">
          <h2 className="text-xl font-bold tracking-tight mb-6" style={{ color: '#0A0A0A' }}>Frequently Asked Questions</h2>
          <div className="space-y-3">
            {[
              { q: 'How can I register for EvalentX?', a: 'Visit our registration page, create an account, form a team of 2-10 members, and complete the payment process.' },
              { q: 'What is the refund policy?', a: 'Please refer to our Terms & Conditions page for detailed information about our refund policy.' },
              { q: 'How can I become a sponsor or partner?', a: 'Use the Investor Corner form above or email us at support@evalentx.com to discuss partnership opportunities.' },
            ].map((faq, i) => (
              <div key={i} className="bg-white p-5" style={{ border: '1px solid #E4E4E7', borderRadius: '2px' }} data-testid={`contact-faq-${i}`}>
                <h3 className="text-sm font-bold mb-1.5" style={{ color: '#0A0A0A' }}>{faq.q}</h3>
                <p className="text-sm" style={{ color: '#71717A', lineHeight: '1.6' }}>{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactPage;
